import React, { Fragment, useEffect, useRef, MouseEventHandler, TouchEventHandler, useState, ReactNode, useCallback } from 'react';
import { GameComponentArgs, GameEnum } from "../../App";
import { Button } from '../shared/button/button';
import './fondue.css';
import PicAFondue from './game-engine/pic-a-fondue';
import { GlobalScoreComponent } from './components/global-score';
import { AnimationContext, getAnimationFunction } from './game-engine/global-animation';


export enum GameStateEnum {
    notStarted = "notStarted",
    idle = "idle",
    insidePot = "inside pot",
    retrieving = "retrieving",
    scores = "scores",
}


export class GameState {
  cheeseQuantity: number | null = null;
  cheeseLevel = 100;
  _gameState: GameStateEnum = GameStateEnum.notStarted;
  picAFondue: PicAFondue = new PicAFondue(this);
  scores: number[] = [];
  onSetGamePhase?: (gamePhase: GameStateEnum) => void;

  mousePos = {x: 0, y: 0};

  get gameState() {
    return this._gameState;
  }

  set gameState(newValue: GameStateEnum) {
    this._gameState = newValue;
    this.onSetGamePhase?.(newValue);
  }
}

export default function Fondue({setCurrentGame}: GameComponentArgs) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const interfaceRef = useRef<HTMLDivElement>(null);
    const gameStateRef = useRef<GameState>(new GameState());
    const animationContextRef = useRef<AnimationContext | null>(null);

    const [gamePhase, setGamePhase] = useState<GameStateEnum>(GameStateEnum.notStarted);
    const [finalScores, setFinalScores] = useState<number[]>([]);
    const [scoresAreDisplayed, setScoresAreDisplayed] = useState<boolean>(false);
    const [warning, setWarning] = useState<string | null>(null);
    
    const getGameState = () => gameStateRef.current;
    getGameState().onSetGamePhase = setGamePhase;

    const startAnimation = useCallback(() => {
        const gameState = getGameState();
        animationContextRef.current = {...animationContextRef.current as AnimationContext,
            lastTime: Date.now(),
            gameState,
            stop: false,
            onGameEnd: () => {
                gameState.picAFondue.retrieve(false, (scores) => {
                    setFinalScores(scores);
                    setScoresAreDisplayed(true);
                });
            }
        };
        const animate = getAnimationFunction(animationContextRef.current as AnimationContext);
        animate();
    
        setTimeout(() => interfaceRef.current?.focus(), 10);
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
    
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
    
        // Set canvas resolution to match its display size
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
    
        const zoom = canvas.height / 400;
    
        // Disable image smoothing for pixelated rendering
        ctx.imageSmoothingEnabled = false;
    
        animationContextRef.current = {
            lastTime:  Date.now(), 
            ctx, 
            gameState: getGameState(), 
            stop: false,
            setWarning, 
            zoom,
            onGameEnd: () => {}
        };

        startAnimation();

        return () => {
            if (animationContextRef.current) {
                animationContextRef.current.stop = true;
            }
        };
    }, [setWarning, startAnimation]);
    
    

    const goToHome = () => {
        setCurrentGame(GameEnum.Home);
    }

    const moveInsideThePot = (clientX: number, clientY: number) => {
        const gameState = getGameState();
        if (gameState.gameState !== GameStateEnum.insidePot) return;
        gameState.mousePos.x = clientX;
        gameState.mousePos.y = clientY;
    }

    const onInterfaceMove: MouseEventHandler = (e) => {
        moveInsideThePot(e.clientX, e.clientY)
    } 
    
    const onInterfaceTouchMove: TouchEventHandler = (e) => {
        moveInsideThePot(e.touches[0].pageX, e.touches[0].pageY)
    }

    const onInterfaceClick = () => {
        const gameState = getGameState();
        switch (gameState.gameState) {
            case GameStateEnum.notStarted:
            case GameStateEnum.idle:
                gameState.picAFondue.startMoving();
                break;
            case GameStateEnum.insidePot:
                gameState.picAFondue.retrieve();
                break;
        }
    }

    const retry = () => {
        setScoresAreDisplayed(false);
        setFinalScores([]);
        setWarning(null);
        gameStateRef.current = new GameState();
        const gameState = gameStateRef.current;
        gameState.onSetGamePhase = setGamePhase;
        gameState.gameState = GameStateEnum.notStarted;

        startAnimation();
    }

    let consigne = "";
    if (gamePhase === GameStateEnum.idle || gamePhase === GameStateEnum.notStarted) {
        consigne = "clique pour tremper le bout de pain";
    }
    if (gamePhase === GameStateEnum.insidePot) {
        consigne = "clique pour retirer le bout de pain";
    }

    let overlayElement: ReactNode = undefined;
    if (!scoresAreDisplayed) {
        overlayElement = <div
            className='overlay'
            ref={interfaceRef}
            onMouseMove={onInterfaceMove}
            onTouchMove={onInterfaceTouchMove}
            onClick={onInterfaceClick}
            tabIndex={0}
        />;
    }


    let centerElements: ReactNode = undefined;
    if (scoresAreDisplayed) {
        centerElements = <Fragment>
        <GlobalScoreComponent scores={finalScores} getScoreFromCheese={getGameState().picAFondue.getScoreFromCheese}/>
        <Button className="retry-button" onClick={retry}>Refaire une fondue !</Button>
    </Fragment>
    }

    return <Fragment>
        <div className="canvas-container fondue">
            <canvas ref={canvasRef} />
        </div>
        <div className="interface fondue">
            {overlayElement}
            <div className='top'>
                <div className="title-container">
                    <div className='title'>Tourne le pic à fondue pour ramasser du fromage</div>
                </div>
                <div className="top-bar">
                    <div className='consigne'>
                        Si tu tournes trop vite, le bout de pain risque de tomber dans le caquelon !<br/>
                        {consigne}<br/>
                        {warning}
                    </div>
                </div>
            </div>
            <div className='center'>
                {centerElements}
            </div>
            <div className='bottom'>
                <Button className="go-back-button" onClick={goToHome}>Retourner à Cervens</Button>
            </div>
        </div>
    </Fragment>
}